import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { message } from "antd";
import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import "./MedicalQuestionsDoctor.css";
import DoctorQuestionsCard from "../../components/MedicalQuestionsDoctor/DoctorQuestionsCard";
import MonthCalendar from "../../components/HomePage/MonthCalendar/MonthCalendar";
import InviteFriendsSection from "../../components/HomePage/InviteFriendsSection/InviteFriendsSection";
import AdBanner from "../../components/HomePage/AdBanner/AdBanner";
import MedilinkInfoCard from "../../components/FeedPage/MedilinkInfoCard/MedilinkInfoCard";
import usePosts from "../../hooks/usePosts";
import { getCurrentDoctor } from "../../../authDoctor/redux/thunks/doctorsThunks";

/* ─────────────────────────────────────────────
   ID helpers
   ⚠️ toIntOrUndef d'origine rejetait tout ID qui n'était
   ni un UUID ni un entier positif (ex: ObjectId Mongo 24 hex,
   ID numérique envoyé en string bizarre, etc). Résultat :
   currentDoctorId restait undefined => like/comment bloqués.
───────────────────────────────────────────── */
const isUUID = (v) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(
    String(v).toLowerCase()
  );

const isMongoId = (v) => /^[0-9a-f]{24}$/i.test(String(v).trim());

const toIdOrUndef = (v) => {
  if (v === null || v === undefined) return undefined;
  const s = String(v).trim();
  if (!s) return undefined;
  const low = s.toLowerCase();
  if (["null", "undefined", "n/a", "na"].includes(low)) return undefined;
  if (isUUID(s)) return low;
  if (isMongoId(s)) return low;
  const n = Number(s);
  if (Number.isFinite(n) && n > 0) return n;
  // Dernier recours : accepter tout autre identifiant alphanumérique plausible
  // plutôt que de le rejeter silencieusement.
  if (/^[a-zA-Z0-9_-]{2,}$/.test(s)) return s;
  return undefined;
};

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */
const MedicalQuestions = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const highlightPostId = searchParams.get("postId");

  const { posts, loading, error, hasPosts, handleFetchPosts, handleRefreshPosts, displayedPosts, hasMore, loadingMore, handleLoadMore } =
    usePosts();

  const [doctor, setDoctor]             = useState(null);
  const [doctorLoading, setDoctorLoading] = useState(true);
  const [doctorErr, setDoctorErr]       = useState("");

  useEffect(() => {
    if (!highlightPostId || loading) return undefined;
    const exists = (displayedPosts || []).some(
      (post) =>
        String(post?.postId || post?.id_post || post?._id || post?.id || "") ===
        String(highlightPostId)
    );
    if (!exists) {
      if (displayedPosts?.length || !hasMore) message.info("This post is no longer available.");
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const target = Array.from(
        document.querySelectorAll("[data-post-id]")
      ).find((element) => element.dataset.postId === String(highlightPostId));
      if (!target) {
        message.info("This post is no longer available.");
        return;
      }
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [displayedPosts, hasMore, highlightPostId, loading]);

  /* ── fetch current doctor ── */
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setDoctorLoading(true);
        setDoctorErr("");
        const d = await dispatch(getCurrentDoctor()).unwrap();
        if (!mounted) return;

        setDoctor(d || null);
      } catch (e) {
        if (!mounted) return;
        setDoctor(null);
        setDoctorErr(String(e?.message || e || "Failed to load doctor"));
        console.warn("❌ getCurrentDoctor failed:", e);
      } finally {
        if (mounted) setDoctorLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [dispatch]);


  const currentDoctorId = useMemo(() => {
    if (!doctor) return undefined;

    // ⚠️ Liste de candidats élargie : on tente un maximum de chemins
    // possibles pour trouver l'ID réel renvoyé par l'API (le nom du
    // champ varie souvent entre backend / DTO / raw payload).
    const candidates = [
      doctor?.pgId,
      doctor?.doctorId,
      doctor?.id,
      doctor?._id,
      doctor?.userId,
      doctor?.raw?.pgId,
      doctor?.raw?.doctorId,
      doctor?.raw?.id,
      doctor?.raw?._id,
      doctor?.raw?.userId,
    ];

    for (const c of candidates) {
      const id = toIdOrUndef(c);
      if (id !== undefined) return id;
    }

    console.warn("⚠️ Impossible de résoudre currentDoctorId depuis:", doctor);
    return undefined;
  }, [doctor]);

  const currentDoctorName = useMemo(() => {
    return (
      (doctor?.fullName && String(doctor.fullName).trim()) ||
      (doctor?.raw?.fullName && String(doctor.raw.fullName).trim()) ||
      "Doctor"
    );
  }, [doctor]);

  const currentDoctorAvatar = useMemo(() => {
    return (
      doctor?.avatar  ||
      doctor?.photo   ||
      doctor?.raw?.avatar ||
      doctor?.raw?.photo  ||
      null
    );
  }, [doctor]);

  const postsSectionRef = useRef(null);
  const bottomRef = useRef(null);
  const [hasRequestedPosts, setHasRequestedPosts] = useState(false);

  // Initial posts load
  useEffect(() => {
    if (hasRequestedPosts) return;
    setHasRequestedPosts(true);
    handleFetchPosts().catch(() => {});
  }, [hasRequestedPosts, handleFetchPosts]);

  const hasAnyPosts = (displayedPosts?.length || 0) > 0 || hasPosts;
  const isInitialLoading = !hasRequestedPosts || (loading && displayedPosts.length === 0 && !error);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || loadingMore) return undefined;
    if (!bottomRef.current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0.1,
      }
    );

    observer.observe(bottomRef.current);
    return () => observer.disconnect();
  }, [handleLoadMore, hasMore, loadingMore]);

  const handleRefresh  = () => handleRefreshPosts();
  const handleTranslate = async (text) => text;

  /* ─────────────────────────────────────────────
     Loading
  ───────────────────────────────────────────── */
  if (isInitialLoading) {
    return (
      <AppLayoutDoctor>
        <div className="mqd-content">
          <div className="feed-container">
            <div className="main-question">
              <div className="mq-loading">
                <div className="spinner"></div>
                <p>Chargement des questions médicales...</p>
              </div>
            </div>
          </div>
        </div>
      </AppLayoutDoctor>
    );
  }

  /* ─────────────────────────────────────────────
     Error
  ───────────────────────────────────────────── */
  if (error && !hasAnyPosts) {
    return (
      <AppLayoutDoctor>
        <div className="mqd-content">
          <div className="feed-container">
            <div className="main-content">
              <div className="mq-error">
                <span className="error-icon">⚠️</span>
                <p className="error-message">{error}</p>
                <button onClick={handleRefresh} className="retry-button">
                  Réessayer
                </button>
              </div>
            </div>
          </div>
        </div>
      </AppLayoutDoctor>
    );
  }

  /* ─────────────────────────────────────────────
     Empty
  ───────────────────────────────────────────── */
  if (!loading && !hasAnyPosts) {
    return (
      <AppLayoutDoctor>
        <div className="mqd-content">
          <div className="feed-container">
            <div className="main-content">
              <div className="mq-empty">
                <span className="empty-icon">📭</span>
                <p className="empty-message">Aucune question médicale pour le moment.</p>
                <button onClick={handleRefresh} className="refresh-button">
                  Actualiser
                </button>
              </div>
              {doctorErr && (
                <div className="mq-error" style={{ marginTop: 12 }}>
                  <span className="error-icon">⚠️</span>
                  <p className="error-message">{doctorErr}</p>
                </div>
              )}
            </div>
            <div className="sidebar">
              <div className="calendar-section"><MonthCalendar /></div>
              <div className="ad-banner-section"><AdBanner /></div>
              <div className="invite-friends-section"><InviteFriendsSection /></div>
              <MedilinkInfoCard />
            </div>
          </div>
        </div>
      </AppLayoutDoctor>
    );
  }

  return (
    <AppLayoutDoctor>
      <div className="mqd-content">
        <div className="feed-container">
          <div className="main-content">
            {doctorErr && (
              <div className="mq-error" style={{ marginBottom: 12 }}>
                <span className="error-icon">⚠️</span>
                <p className="error-message">{doctorErr}</p>
              </div>
            )}

            {doctorLoading && (
              <div className="mq-loading" style={{ marginBottom: 16 }}>
                <div className="spinner"></div>
                <p>Chargement du profil médecin...</p>
              </div>
            )}

            <div className="posts-section" ref={postsSectionRef}>
              {displayedPosts.map((post) => (
                <div
                  key={post.id || post._id || post.id_post}
                  className="post-item"
                  data-post-id={post.postId || post.id_post || post._id || post.id}
                  style={
                    String(post.postId || post.id_post || post._id || post.id) === String(highlightPostId)
                      ? { outline: "3px solid #1677ff", borderRadius: 12 }
                      : undefined
                  }
                >
                  <DoctorQuestionsCard
                    post={post}
                    onTranslate={handleTranslate}
                    currentDoctorId={currentDoctorId}
                    currentDoctorName={currentDoctorName}
                    currentDoctorAvatar={currentDoctorAvatar}
                  />
                </div>
              ))}

              <div ref={bottomRef} className="mq-load-more-sentinel" />
            </div>
          </div>

          <div className="mqd-sidebar">
            <div className="calendar-section"><MonthCalendar /></div>
            <div className="ad-banner-section"><AdBanner /></div>
            <div className="invite-friends-section"><InviteFriendsSection /></div>
            <MedilinkInfoCard />
          </div>
        </div>
      </div>
    </AppLayoutDoctor>
  );
};

export default MedicalQuestions;